import React, { useState, useCallback } from 'react';
import { WikiPage, CreateWikiPageDto, UpdateWikiPageDto } from '../../types/wiki';
import { RichTextEditor } from './RichTextEditor';
import { Button, Field, Input } from '../ui';

interface WikiPageFormProps {
  wikiPage?: WikiPage;
  entityType: 'stakeholder' | 'organization';
  entityId: string;
  onSubmit: (data: CreateWikiPageDto | UpdateWikiPageDto) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  className?: string;
}

export const WikiPageForm: React.FC<WikiPageFormProps> = ({
  wikiPage,
  entityType,
  entityId,
  onSubmit,
  onCancel,
  isLoading = false,
  className = '',
}) => {
  const [formData, setFormData] = useState({
    title: wikiPage?.title || '',
    content: wikiPage?.content || '',
    changeDescription: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const isEditing = !!wikiPage;
  const busy = isLoading || submitting;

  const handleInputChange = useCallback((field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 200) {
      newErrors.title = 'Title must be less than 200 characters';
    }

    // Check if content is effectively empty (Quill may return empty HTML)
    const strippedContent = formData.content
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
    if (!strippedContent) {
      newErrors.content = 'Content is required';
    }

    if (isEditing && !formData.changeDescription.trim()) {
      newErrors.changeDescription = 'Add a change note';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, isEditing]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);
    try {
      if (isEditing) {
        const updateData: UpdateWikiPageDto = {
          title: formData.title,
          content: formData.content,
          changeDescription: formData.changeDescription,
        };
        await onSubmit(updateData);
      } else {
        const createData: CreateWikiPageDto = {
          title: formData.title,
          content: formData.content,
          entityType,
          entityId,
        };
        await onSubmit(createData);
      }
    } catch (error) {
      console.error('Error submitting wiki page:', error);
    } finally {
      setSubmitting(false);
    }
  }, [formData, validateForm, onSubmit, isEditing, entityType, entityId]);

  return (
    <form onSubmit={handleSubmit} noValidate className={`font-ui text-ink ${className}`}>
      <div className="space-y-4">
        <Field label="Title" htmlFor="wiki-title" error={errors.title}>
          <Input
            id="wiki-title"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder="Untitled"
            aria-invalid={!!errors.title}
            disabled={busy}
          />
        </Field>

        <Field label="Content" error={errors.content}>
          <RichTextEditor
            value={formData.content}
            onChange={(value) => handleInputChange('content', value)}
            disabled={busy}
            className={errors.content ? 'ring-2 ring-primary-600/30' : ''}
          />
        </Field>

        {isEditing && (
          <Field label="Change note" htmlFor="wiki-change-desc" error={errors.changeDescription} required>
            <Input
              id="wiki-change-desc"
              value={formData.changeDescription}
              onChange={(e) => handleInputChange('changeDescription', e.target.value)}
              placeholder="What changed?"
              aria-invalid={!!errors.changeDescription}
              disabled={busy}
            />
          </Field>
        )}
      </div>

      {/* Actions: a frosted bar above the tab bar on phones, a plain row from `sm` up */}
      <div
        className="sticky z-20 -mx-4 mt-5 flex items-center justify-end gap-2 border-t border-neutral-200/70 bg-white/85 px-4 py-3 backdrop-blur-xl backdrop-saturate-150 sm:static sm:mx-0 sm:mt-6 sm:border-0 sm:bg-transparent sm:p-0 sm:backdrop-blur-none"
        style={{ bottom: 'var(--app-tabbar-h, 0px)' }}
      >
        <Button variant="secondary" onClick={onCancel} disabled={busy} className="flex-1 sm:flex-none">
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={busy} className="flex-1 sm:flex-none">
          {isEditing ? 'Save' : 'Create'}
        </Button>
      </div>
    </form>
  );
};
